import { useMemo, useState } from "react"
import { PencilSimple, Plus, Package } from "@phosphor-icons/react"

import { cn } from "~/utils/helpers"
import {
  formatPackageDate,
  isPackageOpen,
  PACKAGE_STATUS_META,
  PACKAGE_TYPE_LABEL,
} from "./packageDisplay"
import { COUNTRY_TO_ZONE, type RangePackage } from "./types"
import { useAllocationPackages } from "./useAllocationData"
import PackageWizard, { type PackageWizardMode } from "./PackageWizard"
import { StoreClassBadge } from "./SharedComponents"
import { Badge } from "~/components/design-system/components"
import { Button } from "~/components/design-system/components"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import SearchField from "~/components/planning/SearchField"
import { matchesLineSearch, VARIANT_SEARCH_OPTIONS } from "~/utils/lineSearch"
import { useVariants } from "~/hooks/useVariants"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import {
  StoreAssignmentTable,
  type StoreAssignmentColumn,
  type StoreAssignmentRow,
} from "./StoreAssignmentTable"
import { PackageStoresCommandbar, usePackageStoresDraft } from "./setup/setupDrafts"
import { useLineSearch } from "./useLineSearch"

type TypeFilter = "All" | RangePackage["type"]

const NOT_ACTIVE_REASON = "This package is not active, so it can't be changed."

const PACKAGE_RANGE_SEARCH_ID = "allocation-package-range"

function getProductsForPackage(pkg: RangePackage, variants: IVariantSummary[]) {
  const ids = new Set(pkg.productIds)
  return variants
    .filter((v) => ids.has(v.variantId))
    .map((v) => ({
      id: v.variantId,
      name: v.variantDisplayName,
      category: v.category ?? "Other",
      variant: v,
    }))
}

// ─────────────────────────────────────────────────────────────────────────────
// PackagesSidebar
// ─────────────────────────────────────────────────────────────────────────────

function PackagesSidebar({
  packages,
  selectedId,
  onSelect,
  typeFilter,
  onTypeFilterChange,
  search,
  onSearchChange,
  onNewPackage,
}: {
  packages: RangePackage[]
  selectedId: string | null
  onSelect: (id: string) => void
  typeFilter: TypeFilter
  onTypeFilterChange: (f: TypeFilter) => void
  search: string
  onSearchChange: (s: string) => void
  onNewPackage: () => void
}) {
  const filtered = packages.filter((pkg) => {
    if (typeFilter !== "All" && pkg.type !== typeFilter) return false
    if (search && !pkg.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  return (
    <div className="flex w-[280px] shrink-0 flex-col border-r border-border-base">
      <div className="border-b border-border-base p-3">
        <SearchField
          initialSearchText={search}
          onSearchTextChange={onSearchChange}
          placeholder="Search packages..."
          showInfoElement={false}
          debounceTime={200}
          containerClassName="w-full"
        />
        <div className="mt-2 flex gap-1">
          {(["All", "noos", "seasonal"] as const).map((f) => (
            <Button
              key={f}
              variant="transparent"
              size="sm"
              onClick={() => onTypeFilterChange(f)}
              className={cn(
                "px-2.5 py-1 text-xs",
                typeFilter === f && "bg-primary-100 text-primary-foreground",
              )}
            >
              {f === "All" ? f : PACKAGE_TYPE_LABEL[f]}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.map((pkg) => (
          <button
            key={pkg.id}
            type="button"
            onClick={() => onSelect(pkg.id)}
            className={cn(
              "flex w-full flex-col gap-1 border-b border-border-base px-3 py-2.5 text-left transition-colors",
              selectedId === pkg.id ? "bg-primary-50 dark:bg-primary-950" : "hover:bg-subtle",
            )}
          >
            <div className="flex items-center gap-2">
              <span className="flex-1 truncate text-sm font-medium text-fg-base">{pkg.name}</span>
              <Badge variant={PACKAGE_STATUS_META[pkg.status].variant}>
                {PACKAGE_STATUS_META[pkg.status].label}
              </Badge>
            </div>
            <span className="text-xs text-fg-subtle">
              {pkg.productIds.length} products &middot; {pkg.storeIds.length} stores
            </span>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="p-4 text-center text-sm text-fg-subtle">No packages found</p>
        )}
      </div>

      <div className="border-t border-border-base p-3">
        <Button
          variant="neutral"
          className="w-full gap-1.5"
          wrapperClassName="w-full"
          size="sm"
          requires="edit"
          onClick={onNewPackage}
        >
          <Plus size={14} />
          New Package
        </Button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PackageDetail
// ─────────────────────────────────────────────────────────────────────────────

function PackageHeader({ pkg }: { pkg: RangePackage }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <h2 className="heading-sm text-fg-base">{pkg.name}</h2>
        <Badge variant={PACKAGE_STATUS_META[pkg.status].variant}>
          {PACKAGE_STATUS_META[pkg.status].label}
        </Badge>
        <Badge variant={pkg.type === "noos" ? "emphasizedBlue" : "violet"}>
          {PACKAGE_TYPE_LABEL[pkg.type]}
        </Badge>
      </div>

      {pkg.type === "seasonal" ? (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-fg-subtle">
          <span>
            <span className="font-medium text-fg-base">Start date:</span>{" "}
            {pkg.startDate ? formatPackageDate(pkg.startDate) : "Not set"}
          </span>
          <span>
            <span className="font-medium text-fg-base">Replenishment cut-off:</span>{" "}
            {pkg.cutoffDate ? formatPackageDate(pkg.cutoffDate) : "Not set"}
          </span>
        </div>
      ) : (
        <p className="mt-3 text-xs text-fg-subtle">Always open</p>
      )}
    </div>
  )
}

function PackageRange({
  pkg,
  disabledReason,
  onEditRange,
}: {
  pkg: RangePackage
  disabledReason: string | undefined
  onEditRange: () => void
}) {
  const variants = useVariants()
  const products = useMemo(() => getProductsForPackage(pkg, variants), [pkg, variants])
  const { search, setSearch, searchBy, setSearchBy } = useLineSearch(
    PACKAGE_RANGE_SEARCH_ID,
    VARIANT_SEARCH_OPTIONS,
  )

  const filtered = useMemo(
    () => products.filter((p) => matchesLineSearch(p, search, searchBy, (row) => row.variant)),
    [products, search, searchBy],
  )

  const grouped = useMemo(() => {
    const map = new Map<string, typeof products>()
    for (const p of filtered) {
      const list = map.get(p.category) ?? []
      list.push(p)
      map.set(p.category, list)
    }
    return map
  }, [filtered])

  return (
    <section className="mt-6">
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-semibold text-fg-subtle">
          Products in package ({products.length})
        </h3>
        <div className="flex-1" />
        <ExpandableSearchField
          initialSearchText={search}
          onSearchTextChange={setSearch}
          placeholder="Search..."
          debounceTime={200}
          searchBy={searchBy}
          onSearchByChange={setSearchBy}
          searchByOptions={VARIANT_SEARCH_OPTIONS}
        />
        <Button
          variant="transparent"
          size="xs"
          className="gap-1"
          requires="edit"
          disabled={disabledReason !== undefined}
          allowedTitle={disabledReason}
          onClick={onEditRange}
        >
          <PencilSimple size={12} />
          Edit range
        </Button>
      </div>

      <div className="mt-3 space-y-4">
        {[...grouped.entries()].map(([category, items]) => (
          <div key={category}>
            <div className="mb-1.5 flex items-baseline gap-2">
              <h4 className="text-xs font-semibold text-fg-base">{category}</h4>
              <span className="text-[11px] text-fg-subtle">{items.length} products</span>
            </div>
            <div className="space-y-1">
              {items.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 rounded-md border border-border-base px-3 py-1.5"
                >
                  <span className="flex-1 text-sm text-fg-base">{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {grouped.size === 0 && (
          <p className="text-sm text-fg-subtle">
            {products.length === 0
              ? "No products in this package yet. Use Edit range to pick them."
              : "No products match your search."}
          </p>
        )}
      </div>
    </section>
  )
}

type PackageAssignmentRow = StoreAssignmentRow & {
  classification: string
  size: string
  format: string
  zone: string
  floorArea: number
}

const PACKAGE_ASSIGNMENT_COLUMNS: StoreAssignmentColumn<PackageAssignmentRow>[] = [
  {
    field: "classification",
    name: "Store grade",
    type: "string",
    className: "w-16",
    render: (row) => <StoreClassBadge classification={row.classification} />,
  },
  { field: "size", name: "Store size", type: "string", className: "w-20" },
  { field: "format", name: "Store format", type: "string", className: "w-24" },
  { field: "city", name: "City", type: "string", className: "w-24 text-fg-muted" },
  { field: "zone", name: "Zone", type: "string", className: "w-14" },
  {
    field: "floorArea",
    name: "Selling area (m2)",
    type: "number",
    className: "w-24 text-right tabular-nums",
  },
]

function PackageStores({
  pkg,
  disabledReason,
}: {
  pkg: RangePackage
  disabledReason: string | undefined
}) {
  const draft = usePackageStoresDraft()
  const allStores = draft.stores
  const assignedIds = draft.storeIdsOf(pkg.id)
  const assignStores = (storeIds: string[], assign: boolean) =>
    draft.toggle(pkg.id, storeIds, assign)

  const rows: PackageAssignmentRow[] = allStores.map((store) => ({
    id: store.id,
    storeName: store.storeNumber,
    city: store.city,
    checked: assignedIds.has(store.id),
    changed: assignedIds.has(store.id) !== draft.isSaved(pkg.id, store.id),
    classification: store.classification,
    size: store.size,
    format: store.type,
    zone: store.zone ?? COUNTRY_TO_ZONE[store.country] ?? "—",
    floorArea: store.floorArea,
  }))

  return (
    <section className="mt-8">
      <h3 className="mb-3 text-xs font-semibold text-fg-subtle">Stores</h3>
      <StoreAssignmentTable
        scopeKey="allocation-planning.package-stores"
        note="An assigned store is expected to carry the full range of this package. If a store should carry only part of it, create a separate package with fewer products instead."
        rows={rows}
        columns={PACKAGE_ASSIGNMENT_COLUMNS}
        targetLabel="this package"
        disabledReason={disabledReason}
        loading={draft.storesLoading}
        onAssign={assignStores}
      />
    </section>
  )
}

function PackageDetail({ pkg, onEditRange }: { pkg: RangePackage; onEditRange: () => void }) {
  const disabledReason = isPackageOpen(pkg.status) ? undefined : NOT_ACTIVE_REASON
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <PackageHeader pkg={pkg} />
      <PackageRange pkg={pkg} disabledReason={disabledReason} onEditRange={onEditRange} />
      <PackageStores pkg={pkg} disabledReason={disabledReason} />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// RangeTab (default export)
// ─────────────────────────────────────────────────────────────────────────────

export default function RangeTab() {
  const [selectedPkgId, setSelectedPkgId] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("All")
  const [search, setSearch] = useState("")
  const [wizardMode, setWizardMode] = useState<PackageWizardMode | null>(null)

  const { packages, isLoading } = useAllocationPackages()
  const selectedPkg = packages.find((p) => p.id === selectedPkgId) ?? null

  return (
    <div className="flex h-full flex-col">
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-fg-subtle">Loading packages...</p>
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          <PackagesSidebar
            packages={packages}
            selectedId={selectedPkgId}
            onSelect={setSelectedPkgId}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            search={search}
            onSearchChange={setSearch}
            onNewPackage={() => setWizardMode({ kind: "create" })}
          />
          {selectedPkg ? (
            <PackageDetail
              key={selectedPkg.id}
              pkg={selectedPkg}
              onEditRange={() => setWizardMode({ kind: "editRange", pkg: selectedPkg })}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center">
                <Package size={40} className="mx-auto text-fg-subtle" />
                <p className="mt-2 text-sm text-fg-subtle">
                  {packages.length === 0 ? "No packages yet" : "Select a package to view details"}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {wizardMode && (
        <PackageWizard
          mode={wizardMode}
          onClose={() => setWizardMode(null)}
          onCreated={setSelectedPkgId}
        />
      )}

      <PackageStoresCommandbar />
    </div>
  )
}
